import { z } from "zod";

export const canonicalJobTypeKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/)
  .max(120);
export type CanonicalJobTypeKey = z.infer<typeof canonicalJobTypeKeySchema>;

export const fieldPurposeSchema = z.enum([
  "measurement",
  "pricing",
  "scope",
  "feasibility",
  "customer_preference",
  "contractor_fulfillment",
  "manual_review",
]);
export type FieldPurpose = z.infer<typeof fieldPurposeSchema>;

export const readinessRequirementSchema = z.enum([
  "required_for_price",
  "required_for_submission",
  "required_for_contractor_approval",
  "optional",
]);
export type ReadinessRequirement = z.infer<typeof readinessRequirementSchema>;

export const pricePresentationSchema = z.enum(["single", "range", "starting_at"]);
export type PricePresentation = z.infer<typeof pricePresentationSchema>;

export const quotePriceLifecycleSchema = z.enum([
  "collecting",
  "rough_preview_available",
  "price_ready",
  "draft_quote_generated",
  "awaiting_contractor_approval",
  "contractor_adjusted",
  "contractor_approved",
  "returned_for_clarification",
]);
export type QuotePriceLifecycle = z.infer<typeof quotePriceLifecycleSchema>;

export const typedMoneySchema = z.object({
  currency: z.string().length(3),
  minor: z.string().regex(/^-?\d+$/),
  formatted: z.string().min(1),
});
export type TypedMoney = z.infer<typeof typedMoneySchema>;

export const runbookFieldSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]*$/),
  label: z.string().min(1).max(160),
  help: z.string().max(1000).default(""),
  valueType: z.enum([
    "string",
    "boolean",
    "number",
    "measurement",
    "enum",
    "count",
    "wall_segments",
  ]),
  unit: z.string().max(40).nullable().default(null),
  allowedValues: z.array(z.string().min(1).max(120)).default([]),
  purposes: z.array(fieldPurposeSchema).min(1),
  readiness: readinessRequirementSchema,
  question: z.string().min(1).max(500),
  priority: z.number().int().min(0).max(10_000).default(100),
});
export type RunbookField = z.infer<typeof runbookFieldSchema>;

export const derivedMetricSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]*$/),
  unit: z.string().min(1).max(40),
  handler: z.string().regex(/^[a-z][a-z0-9_.-]*$/),
  dependencies: z.array(z.string().min(1)).min(1),
});
export type DerivedMetric = z.infer<typeof derivedMetricSchema>;

export const pricingComponentSchema = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_.-]*$/),
  rateCode: z.string().regex(/^[a-z][a-z0-9_.-]*$/),
  rateCodeSource: z
    .string()
    .regex(/^field:[a-z][a-z0-9_]*$/)
    .nullable()
    .default(null),
  quantitySource: z
    .string()
    .regex(/^(fixed:(?:0|[1-9]\d*)(?:\.\d+)?|field:[a-z][a-z0-9_]*|metric:[a-z][a-z0-9_]*)$/),
  conditionField: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/)
    .nullable()
    .default(null),
  conditionValue: z.union([z.string(), z.number(), z.boolean()]).nullable().default(null),
  customerCategory: z.string().min(1).max(80),
  contractorExplanation: z.string().min(1).max(500),
  adjustmentPolicy: z.enum(["rate_catalog", "none"]).default("rate_catalog"),
  included: z.boolean().default(true),
  blocksAutomatedPricing: z.boolean().default(true),
});
export type PricingComponent = z.infer<typeof pricingComponentSchema>;

export const runbookTestCaseSchema = z.object({
  name: z.string().min(1).max(160),
  fields: z.record(z.string(), z.unknown()),
  expectedMetrics: z.record(z.string(), z.number()).default({}),
  expectedComponents: z.array(z.string()).default([]),
  expectedPriceReady: z.boolean(),
  expectedReviewFlags: z.array(z.string()).default([]),
});
export type RunbookTestCase = z.infer<typeof runbookTestCaseSchema>;

export const contractorJobRunbookSchema = z.object({
  id: z.string().min(1).max(160),
  contractorId: z.string().min(1).max(160),
  canonicalJobType: canonicalJobTypeKeySchema,
  version: z.number().int().positive(),
  status: z.enum(["draft", "published", "retired"]),
  fields: z.array(runbookFieldSchema).min(1),
  derivedMetrics: z.array(derivedMetricSchema),
  pricingRecipe: z.array(pricingComponentSchema),
  pricePresentation: pricePresentationSchema.default("single"),
  customerRoundingIncrementMinor: z.number().int().positive().default(100),
  testCases: z.array(runbookTestCaseSchema).default([]),
});
export type ContractorJobRunbook = z.infer<typeof contractorJobRunbookSchema>;

export const rateCatalogItemSchema = z
  .object({
    code: z.string().regex(/^[a-z][a-z0-9_.-]*$/),
    description: z.string().min(1).max(300),
    category: z.enum([
      "material",
      "labour",
      "removal",
      "disposal",
      "travel",
      "consumables",
      "fee",
      "access",
      "preparation",
    ]),
    unit: z.enum(["square_foot", "linear_foot", "hour", "item", "fixed", "gallon"]),
    priceBasis: z.enum(["cost", "sell_price", "fixed_package"]),
    amountMinor: z.string().regex(/^\d+$/).nullable().default(null),
    legacyLowMinor: z.string().regex(/^\d+$/).nullable().default(null),
    legacyHighMinor: z.string().regex(/^\d+$/).nullable().default(null),
    taxClass: z.string().min(1).max(80),
    markupApplies: z.boolean(),
    materialWasteApplies: z.boolean(),
    measurementAllowanceApplies: z.boolean(),
    contingencyApplies: z.boolean(),
    active: z.boolean().default(true),
  })
  .superRefine((item, context) => {
    const hasSingle = item.amountMinor !== null;
    const hasLegacyRange = item.legacyLowMinor !== null && item.legacyHighMinor !== null;
    if (hasSingle === hasLegacyRange) {
      context.addIssue({
        code: "custom",
        message: "Provide either one amount or a complete legacy low/high range",
      });
    }
  });
export type RateCatalogItem = z.infer<typeof rateCatalogItemSchema>;

export const quoteFieldValueSchema = z.object({
  value: z.unknown(),
  unit: z.string().nullable().default(null),
  source: z.enum([
    "customer",
    "derived",
    "configured_default",
    "future_image_observation",
    "contractor",
  ]),
  status: z.enum(["confirmed", "proposed", "assumed"]),
  evidenceRef: z.string().nullable().default(null),
  confidence: z.number().min(0).max(1).nullable().default(null),
  dependencies: z.array(z.string()).default([]),
});
export type QuoteFieldValue = z.infer<typeof quoteFieldValueSchema>;

export const customerActivityEventSchema = z.object({
  type: z.enum([
    "job_identified",
    "runbook_loaded",
    "facts_recorded",
    "measurement_calculated",
    "review_flagged",
    "pricing_applied",
    "tax_calculated",
    "draft_price_generated",
    "submitted",
  ]),
  message: z.string().min(1).max(500),
  evidenceRefs: z.array(z.string()).default([]),
});
export type CustomerActivityEvent = z.infer<typeof customerActivityEventSchema>;

export const futureImageObservationSchema = z.object({
  attachmentId: z.string().min(1),
  observationType: z.string().min(1).max(120),
  proposedFieldOrMetric: z.string().min(1).max(160),
  confidence: z.number().min(0).max(1),
  evidenceReference: z.string().min(1).max(500),
  confirmationStatus: z.enum([
    "proposed",
    "customer_confirmed",
    "contractor_confirmed",
    "rejected",
  ]),
});
export type FutureImageObservation = z.infer<typeof futureImageObservationSchema>;

export const quoteStreamEventSchema = z.object({
  requestId: z.string().min(1),
  turnId: z.string().min(1),
  sequence: z.number().int().nonnegative(),
  type: z.enum([
    "turn_started",
    "activity",
    "assistant_delta",
    "quote_state_updated",
    "derived_metrics_updated",
    "draft_price_updated",
    "attachment_updated",
    "turn_completed",
    "error",
  ]),
  payload: z.unknown(),
});
export type QuoteStreamEvent = z.infer<typeof quoteStreamEventSchema>;
